/**
 * PlacementUsageAnalytics Service
 * 
 * Analyzes placement data from GET calls to provide smart suggestions.
 * Analyzes both frequency and recency of field values from server data.
 * Performance-optimized with component-level caching in placementTableAgGrid.
 */

import { PlacementDetailsTableService } from './placementDeatilsTable-service';

/**
 * Analyze field usage and return most frequent values
 * Works with extracted placement objects
 */
const analyzeMostFrequent = (extractedPlacements, fieldName) => {
  const valueCounts = {};
  
  extractedPlacements.forEach(extracted => {
    const value = extracted.fields[fieldName];
    if (value && value !== null && value !== undefined && value !== '' && value !== 'NU') {
      const strValue = String(value).trim();
      valueCounts[strValue] = (valueCounts[strValue] || 0) + 1;
    }
  });
  
  // Find most frequent
  let maxCount = 0;
  let mostFrequent = null;
  
  Object.entries(valueCounts).forEach(([value, count]) => {
    if (count > maxCount) {
      maxCount = count;
      mostFrequent = value;
    }
  });
  
  if (!mostFrequent) return null;
  
  return {
    value: mostFrequent,
    count: maxCount,
    percentage: extractedPlacements.length > 0 ? Math.round((maxCount / extractedPlacements.length) * 100) : 0
  };
};

/**
 * Get most recent value for a field (based on created_at or updated_at timestamp)
 * Works with extracted placement objects
 * 
 * @param {Array} sortedPlacements - Placements already sorted by timestamp (most recent first)
 * @param {string} fieldName - Field name to analyze
 * @returns {Object|null} Most recent value data or null
 */
const analyzeMostRecent = (sortedPlacements, fieldName) => {
  // Find first placement with this field value (array is pre-sorted)
  for (const extracted of sortedPlacements) {
    const value = extracted.fields[fieldName];
    if (value && value !== null && value !== undefined && value !== '' && value !== 'NU') {
      const timestamp = new Date(extracted.created_at || extracted.updated_at).getTime();
      const daysAgo = Math.floor((Date.now() - timestamp) / (1000 * 60 * 60 * 24));
      
      return {
        value: String(value).trim(),
        timestamp: timestamp,
        daysAgo: daysAgo
      };
    }
  }
  
  return null;
};

/**
 * Extract field values from placement using taxonomy schema
 * Uses the same method as the dialog component to parse placement data
 * 
 * @param {Object} placement - Raw placement object from GET call
 * @param {Array} taxonomyFields - Taxonomy schema fields
 * @returns {Object} Extracted field values as key-value pairs
 */
const extractFieldValuesFromPlacement = (placement, taxonomyFields) => {
  if (!placement || !taxonomyFields) return {};
  
  try {
    // Use the same extraction method as the dialog component
    const { editFormData } = PlacementDetailsTableService.getFormEditData(
      taxonomyFields,
      placement
    );
    
    // Parameter fields are present directly in the placement object
    // Combine editFormData with placement to get both taxonomy and parameter fields
    return {
      ...editFormData
    };
  } catch (error) {
    console.warn('Error extracting field values from placement:', error);
    return {};
  }
};

/**
 * Analyze placements and generate smart suggestions
 * This is called on each placement GET to analyze fresh data from server
 * 
 * Logic:
 * 1. First tries to analyze placements created/updated by the logged-in user
 * 2. If no user-specific placements found, falls back to analyzing all placements (team-wide)
 * 3. For user-specific analysis: returns both most frequent and most recent values
 * 4. For team-wide analysis: returns only most frequent values (most recent will be empty)
 * 5. Shows suggestions even if only 1 placement exists
 * 
 * @param {Array} allPlacements - Array of placement objects from GET call
 * @param {string} personEmail - Current user's email address
 * @param {string} currentPlacementType - Type of placement being created
 * @param {Array} taxonomyFields - Taxonomy schema fields for extraction
 * @returns {Object|null} Suggestions object or null if insufficient data
 */
export const analyzePlacementsAndGetSuggestions = (
  allPlacements, 
  personEmail, 
  currentPlacementType = null,
  taxonomyFields = null
) => {
    
  if (!allPlacements || allPlacements.length === 0) return null;
  if (!personEmail) return null;
  if (!taxonomyFields) return null; // Need taxonomy to extract fields
  
  // Helper function to extract placements with optional user filter
  const extractPlacementsWithFilter = (filterByUser = true) => {
    const extracted = [];
    
    allPlacements.forEach(placement => {
      // Filter by user if enabled - check both created_by and updated_by fields
      if (filterByUser) {
        const isCreatedByUser = placement.created_by && placement.created_by === personEmail;
        const isUpdatedByUser = placement.updated_by && placement.updated_by === personEmail;
        
        if (!isCreatedByUser && !isUpdatedByUser) return;
      }
      
      // Filter by placement type if specified
      if (currentPlacementType) {
        const currentTypeLC = currentPlacementType.toLowerCase();
        const packageTypeLC = placement.package_type?.toLowerCase();
        const placementTypeLC = placement.placement_type?.toLowerCase();
        const isSocialType = currentTypeLC?.includes('social');
        
        // Map currentPlacementType to valid package_types
        let isValidType = false;
        
        if (currentTypeLC.includes('direct')) {
          // Direct placements include Package and Child types
          const validPackageTypes = ['package', 'child', 'standalone'];
          isValidType = validPackageTypes.includes(packageTypeLC) && (!isSocialType || placementTypeLC?.includes('social'));
        } else if (currentTypeLC.includes('programmatic')) {
          // Programmatic placements include ProgrammaticPackage and DSP types
          const validPackageTypes = ['programmaticpackage', 'dsp'];
          isValidType = validPackageTypes.includes(packageTypeLC) && (!isSocialType || placementTypeLC?.includes('social'));
        } else {
          // For other types (e.g., Standalone), use exact match
          isValidType = packageTypeLC === currentTypeLC;
        }
        
        if (!isValidType) return;
      }
      
      // Extract field values from this placement
      const extractedFields = extractFieldValuesFromPlacement(
        placement,
        taxonomyFields
      );
      
      // Store extracted data with timestamp
      extracted.push({
        fields: extractedFields,
        created_at: placement.created_at,
        updated_at: placement.updated_at,
        rawPlacement: placement
      });
    });
    
    return extracted;
  };
  
  // First, try to filter by logged-in user
  let extractedPlacements = extractPlacementsWithFilter(true);
  let isUserSpecific = true;
  
  // If no user-specific placements found, fallback to all placements
  if (extractedPlacements.length === 0) {
    extractedPlacements = extractPlacementsWithFilter(false);
    isUserSpecific = false;
    
    // If still no placements found, return null
    if (extractedPlacements.length === 0) return null;
  }
  
  // Collect all unique field names from extracted placements
  const allFields = new Set();
  extractedPlacements.forEach(extracted => {
    Object.keys(extracted.fields).forEach(fieldName => {
      if (extracted.fields[fieldName]) {
        allFields.add(fieldName);
      }
    });
  });
  
  // Sort placements once by timestamp (most recent first) for analyzeMostRecent
  // This optimization prevents O(k × n log n) redundant sorting
  const sortedPlacements = isUserSpecific ? [...extractedPlacements].sort((a, b) => {
    const timeA = new Date(a.created_at || a.updated_at || 0).getTime();
    const timeB = new Date(b.created_at || b.updated_at || 0).getTime();
    return timeB - timeA; // Descending order
  }) : [];
  
  // Analyze each field
  const suggestions = {
    mostFrequent: {},
    mostRecent: {},
    metadata: {
      totalPlacements: extractedPlacements.length,
      placementType: currentPlacementType,
      analyzedAt: Date.now(),
      isUserSpecific: isUserSpecific,
      analysisMode: isUserSpecific ? 'user-specific' : 'team-wide'
    }
  };
  
  allFields.forEach(fieldName => {
    const frequent = analyzeMostFrequent(extractedPlacements, fieldName);
    
    if (frequent) {
      suggestions.mostFrequent[fieldName] = frequent;
    }
    
    // Only analyze most recent if we're looking at user-specific placements
    if (isUserSpecific) {
      const recent = analyzeMostRecent(sortedPlacements, fieldName);
      if (recent) {
        suggestions.mostRecent[fieldName] = recent;
      }
    }
  });
  
  return suggestions;
};

/**
 * Get display name for a field
 * Converts snake_case or database field names to human-readable format
 */
export const getFieldDisplayName = (fieldName) => {
  if (!fieldName) return '';
  
  // Handle Parameter Code fields
  if (fieldName.startsWith('Parameter Code ')) {
    return fieldName.replace('Parameter Code ', '');
  }
  
  // Convert snake_case to Title Case
  if (fieldName.includes('_')) {
    return fieldName
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }
  
  return fieldName;
};

export default {
  analyzePlacementsAndGetSuggestions,
  getFieldDisplayName
};
